import { Typography } from '@material-ui/core'
import React from 'react'

import { TicketRelatedRecord } from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import SectionCard from './SectionCard'

const ENTITY_LABELS = {
  accommodation: 'Accommodation',
  channel: 'Channel',
  provider: 'Provider'
}
const STATUS_LABELS = {
  PENDING: 'Pending',
  RESOLVING: 'Resolving',
  RESOLVED: 'Resolved',
  UNAVAILABLE: 'Unavailable',
  NOT_FOUND: 'Not found'
}

/**
 * WHY SOME RELATED RECORDS SHOW NO IDENTIFIER. This is deliberate, not an omission.
 *
 * The panel splits the fields a related record carries into two classes:
 *
 *   - `entityRef` is SOURCE-AUTHORIZED identity. It is displayed, as "Channel: CH-1".
 *   - `recordId` and `distributionRef` are OPAQUE SOURCE TOKENS, supplied by whoever
 *     reported the ticket. They are never rendered as text. PROMOTION uses `recordId`
 *     only as a link target, never as a visible label.
 *
 * So WALLET_TRANSACTION, RATE_PLAN and CHANNEL_MAPPING render a type and a status and
 * nothing else: every identifier available for them falls on the token side of that line.
 * Four tests in RelatedRecordsPanel.test.tsx enforce this and are named after it, down to
 * fixtures called `private-property-token`.
 *
 * THE KNOWN COST, so nobody has to rediscover it: with two wallet transactions, or two
 * rate plans, on one ticket a support agent cannot tell which is which. That was weighed
 * and accepted (BL-288, 2026-10-21) rather than overlooked. Showing the identifier was
 * considered and rejected, because it would mean displaying data this panel classifies as
 * non-displayable.
 *
 * A link is impossible for WALLET_TRANSACTION in any case: the wallet-history route needs
 * the walletId, and the ticket deliberately never carries it — withholding it is the point
 * of the whole contextual-support mechanism.
 *
 * IF THIS NEEDS REVISITING, the option that does not cross the line is a per-ticket
 * ordinal ("Wallet transaction 1 of 2"), which the create form already uses. Do not reach
 * for recordId or the distribution codes without changing the classification above
 * deliberately, and the tests with it.
 */
export default function RelatedRecordsPanel({
  records
}: {
  records: TicketRelatedRecord[]
}) {
  if (!records.length) return null
  return (
    <SectionCard heading={getTranslate('Related records')} level="h3" quiet>
      {records.map((record) => (
        <div key={record.id} style={{ overflowWrap: 'anywhere' }}>
          <Typography variant="body2">
            {record.refType === 'PROMOTION' ? (
              record.recordId ? (
                <LocalizedLink
                  to={`/promotions/${encodeURIComponent(record.recordId)}`}>
                  {getTranslate('Promotion')}
                </LocalizedLink>
              ) : (
                getTranslate('Promotion')
              )
            ) : record.refType === 'WALLET_TRANSACTION' ? (
              getTranslate('Wallet transaction')
            ) : record.refType === 'RATE_PLAN' ? (
              getTranslate('Rate plan')
            ) : record.refType === 'CHANNEL_MAPPING' ? (
              getTranslate('Channel mapping')
            ) : record.entityRef ? (
              <>
                {getTranslate(
                  ENTITY_LABELS[
                    record.entityRef.type as keyof typeof ENTITY_LABELS
                  ] || record.entityRef.type
                )}
                : {record.entityRef.code}
              </>
            ) : (
              getTranslate('Related record')
            )}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {getTranslate(STATUS_LABELS[record.status])}
          </Typography>
        </div>
      ))}
    </SectionCard>
  )
}
